import { useState } from "react";
import { Checkbox } from "./checkbox";
import { Button } from "./button";
import { Trash2, X, CheckSquare } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";

export function BulkSelectBar({ 
  selectedCount, 
  totalCount,
  onSelectAll, 
  onDeselectAll, 
  onDelete,
  isDeleting = false,
  itemName = "élément"
}) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  if (selectedCount === 0) return null;
  
  return (
    <>
      <div className="sticky top-0 z-10 bg-primary text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between shadow-lg animate-in slide-in-from-top-2">
        <div className="flex items-center gap-4">
          <CheckSquare className="w-5 h-5" />
          <span className="font-medium">
            {selectedCount} {itemName}{selectedCount > 1 ? "s" : ""} sélectionné{selectedCount > 1 ? "s" : ""}
          </span>
          {selectedCount < totalCount && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onSelectAll}
              className="text-white hover:bg-white/20"
            >
              Tout sélectionner ({totalCount})
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onDeselectAll}
            className="text-white hover:bg-white/20"
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Supprimer
          </Button>
        </div>
      </div>
      
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedCount} {itemName}{selectedCount > 1 ? "s" : ""} ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function SelectableRow({ 
  isSelected, 
  onSelect, 
  children, 
  className = "" 
}) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Checkbox 
        checked={isSelected} 
        onCheckedChange={onSelect}
        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
      />
      <div className="flex-1">{children}</div>
    </div>
  );
}

export function useSelection(items) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  const selectAll = () => {
    setSelectedIds(new Set(items.map(item => item.id)));
  };
  
  const deselectAll = () => {
    setSelectedIds(new Set());
  };
  
  const isSelected = (id) => selectedIds.has(id);
  
  return {
    selectedIds,
    selectedCount: selectedIds.size,
    toggleSelect,
    selectAll,
    deselectAll,
    isSelected,
    getSelectedIds: () => Array.from(selectedIds)
  };
}
